import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AlertController, ModalController } from '@ionic/angular';
import { ClientConfigurationService } from 'src/app/core/client-configuration/client-configuration.service';
import { ConfigSettings } from 'src/app/core/client-configuration/config-settings.enum';
import { FormHelperService } from 'src/app/core/helpers/form-helper/form-helper.service';
import { Appointment } from 'src/app/core/models/appointment.model';
import { CustomerService } from 'src/app/core/models/customer-service.model';
import { EquipmentAttribute } from 'src/app/core/models/equipment-attribute.model';
import { Equipment, EquipmentCategory } from 'src/app/core/models/equipment-category.model';
import { InventoryAttribute, InventoryItem } from 'src/app/core/models/inventory.model';
import { EquipmentService } from 'src/app/core/services/equipment/equipment.service';
import { InventoryFormHelperService } from 'src/app/core/services/inventory/inventory-form-helper.service';
import { InventoryService } from 'src/app/core/services/inventory/inventory.service';
import { TicketProvisioningService } from 'src/app/core/services/ticket-provisioning/ticket-provisioning.service';

@Component({
	selector: 'app-add-warehouse-inventory-modal',
	templateUrl: './add-warehouse-inventory-modal.component.html',
	styleUrls: ['./add-warehouse-inventory-modal.component.scss'],
})
export class AddWarehouseInventoryModalComponent implements OnInit {

	@Input()
	appointment: Appointment;

	@Input()
	canToggle: boolean;

	pageTitle = 'Add Warehouse Inventory';

	filterForm: FormGroup;
	categoryOptions = [] as EquipmentCategory[];
	equipmentOptions = [] as Equipment[];
	primaryAttributeName: string;
	primaryAttributeId: number;

	dynamicAttributesForm: FormGroup;
	dynamicAttributes = [] as EquipmentAttribute[];
	equipmentAttributes = [] as EquipmentAttribute[];
	serviceOptions = [] as CustomerService[];

	inventoryItem: InventoryItem;
	hasValidated = false;
	disableWarehouseValidation: boolean;

	constructor(
		public formHelper: FormHelperService,
		public inventoryFormHelper: InventoryFormHelperService,
		private modalController: ModalController,
		private formBuilder: FormBuilder,
		private equipmentService: EquipmentService,
		private inventoryService: InventoryService,
		private alertController: AlertController,
		private clientConfig: ClientConfigurationService,
		private ticketProvisioningService: TicketProvisioningService,
	) { }

	async ngOnInit(): Promise<void> {
		this.categoryOptions = await this.equipmentService.getCategories();
		this.equipmentAttributes = await this.equipmentService.getAttributes();
		this.disableWarehouseValidation =
			(await this.clientConfig.getSetting(ConfigSettings.Disable_Warehouse_Validation))?.value === 'True';

		this.filterForm = this.formBuilder.group({
			categoryId: [null, Validators.required],
			equipment: [{ value: null, disabled: true }, Validators.required],
			primaryAttribute: [null, Validators.required],
		});
	}

	onChangeCategory(categoryId: number): void {
		this.filterForm.controls.primaryAttribute.setValue(null);
		this.primaryAttributeName = null;
		this.primaryAttributeId = null;
		this.filterForm.controls.equipment.setValue(null);
		this.filterForm.controls.equipment.enable();
		this.inventoryItem = null;

		this.equipmentOptions = this.categoryOptions.find(c => c.id === categoryId).equipment
			.sort((a, b) => a.name > b.name ? 1 : -1);
	}

	async onChangeEquipment(equipment: Equipment): Promise<void> {
		this.clearDynamicForm();
		if (equipment) {
			this.setPrimaryAttributeName(this.filterForm.controls.categoryId.value, equipment);

			if (this.disableWarehouseValidation) {
				const inventoryItem = await this.createNewInventoryItem(this.filterForm.controls.categoryId.value, equipment);
				await this.setupInventoryItemAndAttributes(inventoryItem);
			}
		}
	}

	async setupInventoryItemAndAttributes(inventoryItem: InventoryItem) {
		this.inventoryItem = inventoryItem;
		this.setDynamicAttributes(this.equipmentAttributes, this.inventoryItem.attributes);
		await this.initDynamicForm(this.dynamicAttributes, this.appointment, this.serviceOptions);
		this.hasValidated = true;
	}

	async createNewInventoryItem(categoryId: number, equipment: Equipment): Promise<InventoryItem> {
		return {
			id: 0,
			statusId: 3,
			status: 'Available',
			originalStatus: 3,
			categoryId: categoryId,
			categoryDesc: this.categoryOptions.find(c => c.id === categoryId).name,
			equipId: equipment.id,
			equipName: equipment.name,
			attributes: await Promise.all(equipment.attributeIds.map(async id => {
				const attribute = await this.equipmentService.getAttributeById(id);
				return {
					id,
					name: attribute.attribute,
					value: attribute.value
				}
			})),
			customerServiceId: 0,
			customerServiceDescription: 'N/A',
		};
	}

	clearDynamicForm(): void {
		this.dynamicAttributes = [];
		this.dynamicAttributesForm = null;
		this.hasValidated = false;
	}

	async setPrimaryAttributeName(categoryId: number, equipment: Equipment): Promise<void> {
		const attribute = await this.equipmentService.getAttributeById(equipment.attributeIds[0]);
		this.primaryAttributeId = attribute.id;
		this.primaryAttributeName = attribute.attribute;
		this.filterForm.controls.primaryAttribute.enable();
		const validators = [Validators.required].concat(this.inventoryFormHelper.getValidatorsByAttribute(attribute));
		this.filterForm.controls.primaryAttribute.setValidators(Validators.compose(validators));
	}

	async validateInventory() {
		this.filterForm = this.formHelper.markAsTouched(this.filterForm);
		if (!this.filterForm.valid) {
			await this.formHelper.alertUser({ header: 'Error' });
			return;
		}

		const form = this.filterForm.value as { categoryId: number, equipment: Equipment, primaryAttribute: string };

		(await this.inventoryService.validateInventory(form.equipment.id, this.primaryAttributeId, form.primaryAttribute))
			.subscribe(
				async (inventoryItem: InventoryItem) => {
					if (!inventoryItem) {
						const createNewItem = await this.promptToCreateNewItem();
						if (!createNewItem) {
							return;
						}

						inventoryItem = await this.createNewInventoryItem(form.categoryId, form.equipment);
					}

					this.filterForm.controls.primaryAttribute.disable();
					await this.setupInventoryItemAndAttributes(inventoryItem);

				},
				async _ => {
					const createNewItem = await this.promptToCreateNewItem();
					if (!createNewItem) {
						return;
					}

					const inventoryItem = await this.createNewInventoryItem(form.categoryId, form.equipment);;
					this.filterForm.controls.primaryAttribute.disable();
					await this.setupInventoryItemAndAttributes(inventoryItem);
				}
			);
	}

	setDynamicAttributes(equipAttributes: EquipmentAttribute[], invAttributes: InventoryAttribute[]) {
		this.dynamicAttributes = this.inventoryFormHelper.getDynamicAttributes(equipAttributes, invAttributes);
	}

	async initDynamicForm(dynamicAttributes: EquipmentAttribute[], appointment: Appointment, services: CustomerService[]) {
		const { formFields, serviceOptions } = await this.inventoryFormHelper
			.buildDynamicForm(dynamicAttributes, appointment, services);

		this.serviceOptions = serviceOptions;
		this.dynamicAttributesForm = this.formBuilder.group(formFields);
	}

	async onClickAddInventory() {
		this.filterForm = this.formHelper.markAsTouched(this.filterForm);
		this.dynamicAttributesForm = this.formHelper.markAsTouched(this.dynamicAttributesForm);
		if (this.filterForm.invalid || this.dynamicAttributesForm.invalid) {
			await this.formHelper.alertUser();
			return;
		}

		if (this.inventoryItem.id === 0 && !this.inventoryItem.attributes[0].value) {
			this.inventoryItem.attributes[0].value = this.filterForm.controls.primaryAttribute.value;
		}

		const form = this.dynamicAttributesForm.value;
		const attributeUpdates = this.inventoryFormHelper.updateInventoryDynamicAttributes(form, this.dynamicAttributes, this.inventoryItem);

		await this.inventoryService.addWarehouseInventory(this.appointment.id, this.inventoryItem, attributeUpdates);
		await this.ticketProvisioningService.addProvision(this.appointment.id, this.inventoryItem);
		await this.inventoryFormHelper.toastAddSuccess();
		await this.dismiss();
	}

	async toggleMode(): Promise<void> {
		this.dismiss({ forcedModal: 'TRUCK' });
	}

	async dismiss(data?: any): Promise<void> {
		await this.modalController.dismiss({
			dismissed: true,
			...data,
		});
	}

	private async alertOffline(): Promise<void> {
		const offlineAlert = await this.alertController.create({
			header: 'Device Offline',
			message: 'Inventory validation is not available while device is offline.',
			buttons: ['OK'],
		});

		await offlineAlert.present();
	}

	async promptToCreateNewItem() {
		const alert = await this.alertController.create({
			header: 'Inventory Not Found',
			message: 'Either you are offline or no inventory was found matching this criteria. ' +
				'Would you like to proceed and create a new piece of inventory?',
			backdropDismiss: false,
			buttons: [
				{
					text: 'NO',
					handler: () => {
						alert.dismiss(false);
						return false;
					}
				},
				{
					text: 'YES',
					handler: () => {
						alert.dismiss(true);
						return false;
					}
				},
			],
		});

		await alert.present();
		return (await alert.onDidDismiss()).data;
	}
}
